import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { MapPin, User, CalendarClock, Inbox, Lightbulb, FileText, ChevronDown, ChevronRight } from "lucide-react";
import { LoadError, errMessage } from "@/components/LoadError";
import { usePersistentCollapse } from "@/hooks/usePersistentCollapse";
import PrepReminder from "@/components/PrepReminder";
import { toast } from "sonner";
import { cpDag, kortDatum, datumKort, todayISO } from "@/components/afspraken/afsprakenUtils";
import { SectieKop } from "@/components/rust/Rust";
import { MijnPuntDialog } from "@/components/MijnPuntDialog";
import { AntwoordVeld, AntwoordLezen } from "@/components/MijnPuntAntwoord";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

const cidOf = (c) => c.id || c.date;

// De bedrijfsleider ziet ALLES over zijn eigen locatie (ook het OM-oordeel), maar mag maar één ding
// schrijven: de notitie op een peilmoment + dat peilmoment aanvinken als "gemeld". Nooit de status.
const STATUS_META = {
  open: { label: "Open — nog te beoordelen", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  nagekomen: { label: "Nagekomen (oordeel OM)", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  niet: { label: "Niet nagekomen (oordeel OM)", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};
const EIGENAAR_LABEL = { bedrijfsleider: "Bedrijfsleider", om: "OM", beide: "Beide" };

function relLabel(a) {
  const dl = a.effectiveDeadline || a.deadline;
  if (!dl) return "Geen deadline";
  const d = new Date(dl + "T00:00:00");
  if (isNaN(d)) return "";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (a.status === "open" && a.overdue && diff < 0) { const n = Math.abs(diff); return `${n} dag${n > 1 ? "en" : ""} te laat`; }
  if (diff === 0) return "Vandaag";
  if (diff === 1) return "Morgen";
  if (diff > 0) return `Over ${diff} dagen`;
  return datumKort(dl);
}

// v22-R1 R1: verstreken, niet-afgevinkt peilmoment: met melding (server: a.meldingen) blauw, zonder rood. Niet bij een afdeling. v23 R3: blauw vervangt "gereed".
function peilStand(a, m, open, today) {
  if (!open || a.eigenaar === "afdeling") return null;
  const cps = a.checkpoints || [];
  const verstreken = m.deadline ? !cps.length && m.cid === "deadline" && a.deadline && a.deadline.slice(0, 10) < today
    : !m.done && m.date && m.date.slice(0, 10) < today;
  if (!verstreken) return null;
  return (m.deadline ? a.wachtOpOm : a.meldingen?.[m.cid]) ? "gemeld" : "gemist";
}

function PeilStaat({ a, m, stand }) {
  if (!stand) return null;
  return stand === "gemeld"
    ? <span data-testid={`bl-gemeld-wacht-${a.id}-${m.cid}`} className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Gemeld · wacht op OM</span>
    : <span data-testid={`bl-gemist-${a.id}-${m.cid}`} className="text-[10px] font-bold uppercase tracking-wider text-rose-600">Gemist</span>;
}

export default function MijnPunten() {
  const { user } = useAuth();
  const [afspraken, setAfspraken] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [eigenLocatie, setEigenLocatie] = useState("");
  const { isCollapsed: reflectieCollapsed, toggle: toggleReflectie } = usePersistentCollapse("bl-reflectie", user.username, true);

  const [afdNamen, setAfdNamen] = useState({});
  const loadMeta = () => api.get("/meta").then((r) => { setEigenLocatie(r.data.eigenLocatie || ""); setAfdNamen(r.data.afdelingNamen || {}); }).catch(() => {});

  const load = () => {
    api.get("/afspraken").then((r) => {
      setAfspraken(r.data);
      setLoaded(true);
      setLoadError("");
    }).catch((e) => setLoadError(errMessage(e)));
  };

  useEffect(() => { loadMeta(); load(); }, []);

  const [dialog, setDialog] = useState({ open: false, punt: null });
  const [weg, setWeg] = useState(null);
  const verwijder = async () => {
    const a = weg;
    if (!a) return;
    setWeg(null);
    try {
      await api.delete(`/mijn-punten/${a.id}`);
      toast.success("Punt weggehaald");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Weghalen mislukt. Probeer het opnieuw.");
    }
    load();
  };

  // v15 A2: de bedrijfsleider schrijft UITSLUITEND in blReported (per deelpunt-id, of "deadline") — nooit in de OM-array.
  const repOf = (a, cid) => (a.blReported && a.blReported[cid]) || {};

  // Groepeer op status: open eerst, daarna afgehandeld — zoals de OM ze ziet.
  const groups = useMemo(() => {
    const isOpen = (a) => (a.status || "open") === "open";   // R8f: zonder status = open, zoals de server
    const open = afspraken.filter(isOpen);
    const closed = afspraken.filter((a) => !isOpen(a));
    const byOverdue = (x, y) => {
      if (!!x.overdue !== !!y.overdue) return x.overdue ? -1 : 1;
      return (x.effectiveDeadline || x.deadline || "9999").localeCompare(y.effectiveDeadline || y.deadline || "9999");
    };
    return [
      { key: "open", label: "Open punten", items: [...open].sort(byOverdue) },
      { key: "afgehandeld", label: "Afgehandeld", items: [...closed].sort((x, y) => (y.timestamp || "").localeCompare(x.timestamp || "")) },
    ].filter((g) => g.items.length > 0);
  }, [afspraken]);

  const reportCheckpoint = async (a, cid, patch) => {
    try {
      const r = await api.patch(`/afspraken/${a.id}/checkpoint`, { id: cid, ...patch });
      setAfspraken((list) => list.map((x) => (x.id === a.id ? { ...x, ...r.data } : x)));
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt. Probeer het opnieuw.");
      if ([404, 409].includes(e?.response?.status)) load();
      return false;
    }
  };

  const renderAfspraak = (a) => {
    const sm = STATUS_META[a.status] || STATUS_META.open;
    const cps = [...(a.checkpoints || [])].sort((x, y) => x.date.localeCompare(y.date));
    return (
      <div key={a.id} data-testid={`bl-afspraak-${a.id}`} className={`bg-white rounded-[10px] border p-3 md:p-4 ${a.overdue ? "border-rose-300 ring-1 ring-rose-200" : "border-zinc-200"}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p data-testid={`bl-wat-${a.id}`} className="text-sm font-semibold text-zinc-900 whitespace-pre-line break-words">{a.wat}</p>
            {a.bron === "locatie" && <span data-testid={`bl-door-jou-${a.id}`} className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider text-amber-700">{a.door === user.username ? "door jou toegevoegd" : "door de locatie toegevoegd"}</span>}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-zinc-500">
              {a.locatie && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{a.locatie}</span>}
              {a.bedrijfsleider && <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" />{a.bedrijfsleider}</span>}
              {a.eigenaar === "afdeling" ? <span data-testid={`bl-bij-afdeling-${a.id}`} className="inline-flex items-center gap-1 font-semibold text-zinc-700">Bij {afdNamen[a.afdeling] || "een afdeling"}</span>
                : <span className="inline-flex items-center gap-1">Eigenaar: <span className="font-semibold text-zinc-500">{EIGENAAR_LABEL[a.eigenaar] || a.eigenaar}</span></span>}
              {a.status === "open" && a.wachtOpOm
                ? <span data-testid={`bl-wacht-op-om-${a.id}`} className="inline-flex items-center gap-1 font-mono whitespace-nowrap text-blue-700 font-semibold"><CalendarClock className="h-3.5 w-3.5" />{a.wachtOpOmLabel}</span>
                : <span data-testid={`bl-termijn-${a.id}`} className={`inline-flex items-center gap-1 font-mono ${a.overdue ? "text-rose-600 font-bold" : ""}`}>
                  <CalendarClock className="h-3.5 w-3.5" />{relLabel(a)}{a.overdue && !relLabel(a).includes("te laat") ? " · te laat" : ""}
                </span>}
            </div>
          </div>
          <span data-testid={`bl-status-${a.id}`} className={`shrink-0 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${sm.cls}`}>{sm.label}</span>
        </div>

        {a.status === "niet" && a.vervolgInfo?.deadline && <p data-testid={`bl-vervolg-${a.id}`} className="mt-3 text-sm text-zinc-700">Niet nagekomen. Nieuwe deadline: <span className="font-semibold">{kortDatum(a.vervolgInfo.deadline)}</span>.</p>}
        {a.vervolgOpInfo && <p data-testid={`bl-vervolg-op-${a.id}`} className="mt-3 text-sm text-zinc-700">Vervolg op een eerdere afspraak (deadline {kortDatum(a.vervolgOpInfo.deadline) || "onbekend"})</p>}

        {a.kanAanpassen && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" data-testid={`bl-punt-bewerken-${a.id}`} onClick={() => setDialog({ open: true, punt: a })}
              className="min-h-[44px] sm:min-h-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-xs font-semibold text-zinc-800 hover:border-zinc-500 transition-colors">
              <Pencil className="h-3.5 w-3.5" /> Bewerken
            </button>
            <button type="button" data-testid={`bl-punt-verwijderen-${a.id}`} onClick={() => setWeg(a)}
              className="min-h-[44px] sm:min-h-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-rose-200 text-xs font-semibold text-rose-700 hover:border-rose-400 hover:bg-rose-50 transition-colors">
              <Trash2 className="h-3.5 w-3.5" /> Verwijderen
            </button>
          </div>
        )}

        {(() => {
          const open = (a.status || "open") === "open";
          const today = todayISO();
          const dl = a.deadline;   // R8d: de eigen deadline van de afspraak (effectiveDeadline = eerstvolgend peilmoment)
          const momenten = [
            ...cps.map((c) => ({ cid: cidOf(c), label: cpDag(c.date), wat: c.wat, date: c.date, done: c.done, testid: `bl-checkpoint-${a.id}-${cidOf(c)}`, deadline: false,
              ids: { note: `bl-checkpoint-note-${a.id}-${cidOf(c)}`, gereed: `bl-checkpoint-toggle-${a.id}-${cidOf(c)}`, opslaan: `bl-checkpoint-opslaan-${a.id}-${cidOf(c)}` } })),
            { cid: "deadline", label: dl ? `Deadline ${cpDag(dl)}` : "Stand van zaken", testid: `bl-deadline-${a.id}`, deadline: true,
              ids: { note: `bl-deadline-note-${a.id}`, gereed: `bl-deadline-gereed-${a.id}`, opslaan: `bl-deadline-opslaan-${a.id}` } },
          ];
          return (
            <div className="mt-3" data-testid={`bl-checkpoints-${a.id}`}>
              {!open && <p data-testid={`bl-afgehandeld-${a.id}`} className="text-xs text-zinc-500 mb-1.5">Afgehandeld door je OM; je antwoorden blijven zichtbaar.</p>}
              <div className="space-y-3 pl-3 border-l-2 border-blue-100">
                {momenten.map((m) => { const stand = peilStand(a, m, open, today); return (
                  <div key={m.cid} data-testid={m.testid} className={m.deadline ? "rounded-md border border-zinc-300 bg-zinc-50 px-3 py-2 -ml-3 border-l-4 border-l-blue-600" : ""}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm ${m.deadline ? "font-bold text-zinc-900" : "font-mono font-semibold text-zinc-700"}`}>{m.label}</span>
                      {(m.wat || "").trim() && <span data-testid={`bl-checkpoint-wat-${a.id}-${m.cid}`} className="text-sm text-zinc-800 break-words">— {m.wat}</span>}
                      {repOf(a, m.cid).reported && stand !== "gemeld" && <span data-testid={`bl-gereed-${a.id}-${m.cid}`} className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">gereed</span>}
                      <PeilStaat a={a} m={m} stand={stand} />
                    </div>
                    {a.eigenaar === "afdeling" ? null : open ? <AntwoordVeld rep={repOf(a, m.cid)} ids={m.ids} onSave={(note) => reportCheckpoint(a, m.cid, { note })}
                        onToggle={() => reportCheckpoint(a, m.cid, { done: !repOf(a, m.cid).reported })} />
                      : <AntwoordLezen rep={a.blReported && a.blReported[m.cid]} testid={`${m.testid}-lezen`} />}
                  </div>
                ); })}
              </div>
            </div>
          );
        })()}

        {(a.samenvatting || a.analyse) && (() => {
          const rc = reflectieCollapsed(a.id);
          return (
            <div className="mt-3" data-testid={`bl-reflectie-${a.id}`}>
              <button data-testid={`bl-reflectie-toggle-${a.id}`} onClick={() => toggleReflectie(a.id)}
                className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500 hover:text-zinc-800 transition-colors">
                {rc ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                <Lightbulb className="h-3.5 w-3.5 text-amber-500" /> Afsluiting door OM
              </button>
              {!rc && (
                <div className="mt-2 space-y-2 pl-2 border-l-2 border-amber-200">
                  {a.samenvatting && (
                    <div>
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-zinc-400"><FileText className="h-3 w-3" /> Samenvatting</div>
                      <p className="text-sm text-zinc-700 whitespace-pre-line break-words">{a.samenvatting}</p>
                    </div>
                  )}
                  {a.analyse && (
                    <div>
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-amber-600"><Lightbulb className="h-3 w-3" /> Kritische analyse</div>
                      <p className="text-sm text-zinc-700 whitespace-pre-line break-words">{a.analyse}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div data-testid="mijn-punten-page">
      <PrepReminder />
      <div className="mb-3 md:mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">Mijn punten</h1>
          <p className="text-sm text-zinc-500">
            De afspraken voor {eigenLocatie ? <span className="font-semibold text-zinc-700">{eigenLocatie}</span> : "jouw locatie"}: geef bij elke deadline en elk peilmoment de stand van zaken. Het oordeel en afsluiten doet de OM.
          </p>
        </div>
        <button type="button" data-testid="bl-punt-toevoegen" onClick={() => setDialog({ open: true, punt: null })}
          className="min-h-[44px] flex items-center gap-1.5 px-4 rounded-md bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors">
          <Plus className="h-4 w-4" /> Punt toevoegen
        </button>
      </div>
      <MijnPuntDialog open={dialog.open} punt={dialog.punt} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={load} />
      <AlertDialog open={!!weg} onOpenChange={(o) => { if (!o) setWeg(null); }}>
        <AlertDialogContent data-testid="bl-punt-verwijder-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Punt weghalen?</AlertDialogTitle>
            <AlertDialogDescription>Het punt verdwijnt van Mijn punten en bij je OM.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bl-punt-verwijder-annuleren" className="min-h-[44px] sm:min-h-0">Annuleren</AlertDialogCancel>
            <AlertDialogAction data-testid="bl-punt-verwijder-bevestigen" onClick={verwijder} className="min-h-[44px] sm:min-h-0 bg-rose-600 hover:bg-rose-700">Weghalen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {loadError && <div className="mb-4"><LoadError error={loadError} onRetry={load} testid="mijn-punten-load-error" /></div>}

      {loadError ? null : !loaded ? (
        <div data-testid="mijn-punten-loading" className="bg-white rounded-[10px] border border-zinc-200 p-12 text-center text-sm text-zinc-400">Laden…</div>
      ) : afspraken.length === 0 ? (
        <div className="bg-white rounded-[10px] border border-zinc-200 p-12 text-center" data-testid="mijn-punten-empty">
          <Inbox className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-500 font-medium">Nog geen punten voor jouw locatie.</p>
          <p className="text-xs text-zinc-400 mt-1">Zodra de OM een afspraak vastlegt of jij zelf een punt toevoegt, verschijnt het hier.</p>
        </div>
      ) : (
        <div className="space-y-3 md:space-y-4">
          {groups.map((g) => (
            <div key={g.key} data-testid={`bl-group-${g.key}`}>
              <SectieKop los titel={g.label} aantal={g.items.length} />
              <div className="tw1-los space-y-3">{g.items.map(renderAfspraak)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
